import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { Form, Input, Button, App, } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { resetPasswordRequest, resetPassword, fetchPasswordSettings } from 'api/user-api';
import IntroScreen from 'components/IntroScreen';
import { txt } from 'libs/i18n';
import styles from './style.module.less';
const extractTokenFromInput = (input) => {
    var _a;
    const trimmed = input.trim();
    if (trimmed.startsWith('ResetPassword_')) {
        return trimmed;
    }
    try {
        const url = new URL(trimmed, window.location.origin);
        if (url.hash === '')
            return null;
        const params = new URLSearchParams(url.hash.slice(1));
        const token = (_a = params.get('token')) !== null && _a !== void 0 ? _a : params.get('resetToken');
        if (token !== null && token.trim() !== '')
            return token;
        return null;
    }
    catch (_b) {
        return null;
    }
};
const getTokenFromLocation = (loc) => {
    var _a, _b;
    const hash = (_a = loc.hash) !== null && _a !== void 0 ? _a : '';
    if (hash === '')
        return null;
    const params = new URLSearchParams(hash.slice(1));
    const token = (_b = params.get('token')) !== null && _b !== void 0 ? _b : params.get('resetToken');
    if (token === null)
        return null;
    const trimmed = token.trim();
    return trimmed !== '' ? trimmed : null;
};
const ResetPasswordScreen = ({ location }) => {
    const history = useHistory();
    const [redirectURL, setRedirectUrl] = useState(null);
    const [tokenFromUrl, setTokenFromUrl] = useState(() => getTokenFromLocation(location));
    const [requiredLength, setRequiredLength] = useState(6);
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const [pasteTokenForm] = Form.useForm();
    const { message } = App.useApp();
    const loginTo = '/projects';
    const { pathname } = location;
    const isSetPasswordPage = pathname === '/login/setpassword';
    const createPassword = pathname === '/signup/createpassword' || isSetPasswordPage;
    const hasTokenInUrl = tokenFromUrl !== null;
    useEffect(() => {
        const getRequiredPwdLength = async () => {
            const passwordSettings = await fetchPasswordSettings();
            setRequiredLength(passwordSettings.requiredLength);
        };
        void getRequiredPwdLength();
    }, []);
    const handleSubmit = async (values) => {
        const result = await resetPasswordRequest(values.email.trim());
        if (result.isSuccess()) {
            void message.success(txt('resetRequestGeneric'), 5);
        }
        else if (result.err() === 'invalid-email') {
            void message.success(txt('resetRequestGeneric'), 5);
        }
        else {
            void message.error(txt('unexpectedError'));
        }
        setIsLoading(false);
        form.resetFields(['email']);
    };
    const handleReset = async (password, resetToken) => {
        setIsLoading(true);
        const result = await resetPassword(password, resetToken);
        if (result.isSuccess()) {
            void message.success(txt('resetOk'), 5);
            setRedirectUrl(loginTo);
        }
        else if (result.err() === 'invalid-token') {
            void message.error(txt('resetTokenInvalid'), 5);
        }
        else if (result.err() === 'used-password') {
            void message.error(txt('usedPassword'), 5);
        }
        else if (result.err() === 'password-too-weak') {
            void message.error(txt('passwordTooWeak'), 5);
        }
        else {
            void message.error(txt('unexpectedError'));
        }
        setIsLoading(false);
        form.resetFields(['password', 'passwordConfirm']);
    };
    const setResetTokenFromUrl = (values) => {
        const resetToken = tokenFromUrl;
        if (resetToken !== null) {
            void handleReset(values.password, resetToken);
        }
        else {
            void message.error(txt('resetTokenInvalid'));
        }
    };
    if (redirectURL !== null) {
        return _jsx(Redirect, { push: true, to: redirectURL });
    }
    const getSubmitButton = (label) => (_jsx("div", { className: styles.controls, children: _jsxs(Button, { className: styles.button, type: "primary", size: "large", htmlType: "submit", disabled: isLoading, children: [isLoading ? _jsx(LoadingOutlined, {}) : null, label] }) }));
    const handlePasteTokenSubmit = (values) => {
        const token = extractTokenFromInput(values.tokenOrUrl);
        if (token !== null) {
            pasteTokenForm.resetFields();
            setTokenFromUrl(token);
        }
        else {
            void message.error(txt('firstLoginInvalidToken'));
        }
    };
    return (_jsx(IntroScreen, { isLogoDisplayed: false, children: _jsxs(_Fragment, { children: [_jsx("h1", { children: createPassword ? txt('createPsw') : txt('resetPsw') }), hasTokenInUrl
                    ? (_jsxs(_Fragment, { children: [createPassword ? null : _jsx("p", { children: txt('pleaseNew') }), _jsxs(Form, { form: form, onFinish: setResetTokenFromUrl, children: [_jsx(Form.Item, { name: "password", className: styles.formInput, hasFeedback: true, rules: [
                                            {
                                                required: true,
                                                message: txt('yourPassword'),
                                            },
                                            {
                                                min: requiredLength,
                                                whitespace: true,
                                                message: `${txt('minChars')}${requiredLength}`,
                                            },
                                            {
                                                max: 128,
                                                message: txt('maxCharacters'),
                                            },
                                        ], children: _jsx(Input.Password, { type: "password", placeholder: txt('pw') }) }), _jsx(Form.Item, { name: "passwordConfirm", className: styles.formInput, dependencies: ['password'], hasFeedback: true, rules: [
                                            {
                                                required: true,
                                                message: txt('yourPassword'),
                                            },
                                            {
                                                min: requiredLength,
                                                whitespace: true,
                                                message: `${txt('minChars')}${requiredLength}`,
                                            },
                                            {
                                                max: 128,
                                                message: txt('maxCharacters'),
                                            },
                                            ({ getFieldValue }) => ({
                                                validator(rule, value) {
                                                    if (!value || getFieldValue('password') === value) {
                                                        return Promise.resolve();
                                                    }
                                                    // eslint-disable-next-line prefer-promise-reject-errors
                                                    return Promise.reject(txt('noMatch'));
                                                },
                                            }),
                                        ], children: _jsx(Input.Password, { type: "password", placeholder: txt('confirmPW') }) }), getSubmitButton(txt('confirmPW'))] })] }))
                    : isSetPasswordPage
                        ? (_jsxs(_Fragment, { children: [_jsx("p", { children: txt('firstLoginPasteTokenText') }), _jsxs(Form, { form: pasteTokenForm, initialValues: { tokenOrUrl: '' }, onFinish: handlePasteTokenSubmit, children: [_jsx(Form.Item, { name: "tokenOrUrl", className: styles.formInput, rules: [
                                                {
                                                    required: true,
                                                    message: txt('firstLoginPasteTokenRequired'),
                                                },
                                            ], children: _jsx(Input.TextArea, { placeholder: txt('firstLoginPasteTokenPlaceholder'), autoSize: { minRows: 2, maxRows: 4 }, autoFocus: true }) }), getSubmitButton(txt('firstLoginContinue'))] })] }))
                        : (_jsxs(_Fragment, { children: [_jsx("p", { children: txt('resetPswText') }), _jsxs(Form, { form: form, initialValues: { email: '' }, onFinish: handleSubmit, children: [_jsx(Form.Item, { name: "email", className: styles.formInput, rules: [
                                                {
                                                    required: true,
                                                    transform: (value) => value.trim(),
                                                    type: 'email',
                                                    message: txt('pleaseEmail'),
                                                },
                                            ], children: _jsx(Input, { placeholder: txt('email'), autoFocus: true, size: "large" }) }), getSubmitButton(txt('resetPsw'))] }), pathname === '/login/resetpassword' && (_jsx("div", { className: styles.loginLink, children: _jsx(Button, { type: "link", onClick: () => history.push('/login/setpassword'), children: txt('firstLogin') }) }))] })), _jsx("div", { className: styles.loginLink, children: _jsx(Button, { type: "link", onClick: () => setRedirectUrl('/login'), children: txt('login') }) })] }) }));
};
export default ResetPasswordScreen;
